/**
 * MappingQueuePanel — экран B админки СЗР: очередь разбора маппинга (AK-385).
 *
 * Оператор видит нераспознанные при импорте термины (raw_text) с типом,
 * fuzzy-кандидатом и числом вхождений. Действие — «привязать» к сущности
 * каталога: открывает EntityPicker по типу термина (crop/pest/disease/weed/
 * treated_object), затем POST /import/queue/{id}/resolve создаёт alias →
 * будущие импорты резолвятся автоматически. Непривязанные не участвуют в
 * пользовательском поиске (FR-20).
 *
 * Фильтр по типу термина (все / культуры / вредн.объекты / объекты обработки).
 */
import { useMemo, useState } from 'react';
import { AlertCircle, Link2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useImportQueue, useResolveQueueItem } from '@/lib/pesticides/queries';
import type { QueueItem } from '@/lib/pesticides/types';
import {
  EntityPicker,
  type EntityOption,
  type EntitySource,
} from '@/pages/reference/pesticides/EntityPicker';
import { toast } from '@/lib/toast';

const TERM_KIND_LABEL: Record<string, string> = {
  crop: 'культура',
  pest: 'вредитель',
  disease: 'болезнь',
  weed: 'сорняк',
  treated_object: 'объект обр.',
};

const FILTERS: { key: string | undefined; label: string }[] = [
  { key: undefined, label: 'Все' },
  { key: 'crop', label: 'Культуры' },
  { key: 'pest', label: 'Вредители' },
  { key: 'disease', label: 'Болезни' },
  { key: 'weed', label: 'Сорняки' },
  { key: 'treated_object', label: 'Объекты обработки' },
];

function sourceForKind(kind: string): EntitySource {
  switch (kind) {
    case 'crop':
      return { path: '/api/v1/reference/crops', translated: true };
    case 'pest':
      return { path: '/api/v1/reference/pests', translated: true };
    case 'disease':
      return { path: '/api/v1/reference/diseases', translated: true };
    case 'weed':
      return { path: '/api/v1/reference/weeds', translated: true };
    case 'treated_object':
    default:
      return {
        path: '/api/v1/reference/pesticides/treated-objects',
        translated: false,
      };
  }
}

/** Первый кандидат из массива (best-effort — форма зависит от резолвера). */
function firstCandidateLabel(item: QueueItem): string | null {
  if (Array.isArray(item.candidates) && item.candidates.length > 0) {
    const c = item.candidates[0];
    const name = c.name ?? '';
    const score = typeof c.score === 'number' ? ` (${Math.round(c.score * 100)}%)` : '';
    return name ? `${name}${score}` : null;
  }
  return null;
}

function ResolveDialog({
  item,
  onClose,
}: {
  item: QueueItem | null;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<EntityOption | null>(null);
  const resolveMut = useResolveQueueItem();

  const source = useMemo(
    () => (item ? sourceForKind(item.term_kind) : null),
    [item],
  );

  const handleBind = async () => {
    if (!item || !choice) return;
    try {
      await resolveMut.mutateAsync({
        queueId: item.id,
        payload: { resolved_id: choice.id, status: 'mapped' },
      });
      toast.success(`«${item.incoming_name}» привязано к «${choice.name}»`);
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Привязать термин</DialogTitle>
          <DialogDescription>
            {item && (
              <>
                «{item.incoming_name}» ·{' '}
                {TERM_KIND_LABEL[item.term_kind] ?? item.term_kind}. Выберите
                запись каталога — создастся alias, будущие импорты подставят её
                автоматически.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {source && (
          <div className="py-2">
            <EntityPicker
              source={source}
              value={choice}
              onChange={setChoice}
              placeholder="Найти запись каталога…"
            />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => void handleBind()} disabled={!choice || resolveMut.isPending}>
            {resolveMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            Привязать
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MappingQueuePanel() {
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [resolving, setResolving] = useState<QueueItem | null>(null);
  const q = useImportQueue(filter, 'pending');
  const items = q.data ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <span className="text-sm text-muted-foreground">
            Непривязано: {items.length}
          </span>
          <div className="ml-auto flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                onClick={() => setFilter(f.key)}
                className={
                  'rounded-md px-2.5 py-1 text-xs transition-colors ' +
                  (filter === f.key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent')
                }
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {q.isError && (
          <div className="flex items-center gap-2 px-4 py-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {q.error?.message ?? 'Не удалось загрузить очередь'}
          </div>
        )}

        <div className="overflow-x-auto">
          <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow>
                <TableHead>raw_text (из каталога)</TableHead>
                <TableHead className="w-[130px]">Тип</TableHead>
                <TableHead className="w-[90px]">Вхождений</TableHead>
                <TableHead className="w-[220px]">Кандидат</TableHead>
                <TableHead className="w-[120px] text-right">Действие</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!q.isLoading &&
                items.map((it) => {
                  const candidate = firstCandidateLabel(it);
                  return (
                    <TableRow key={it.id}>
                      <TableCell className="font-medium text-foreground">
                        {it.incoming_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {TERM_KIND_LABEL[it.term_kind] ?? it.term_kind}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {it.occurrence_count}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {candidate ?? (
                          <span className="italic">нет кандидата</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setResolving(it)}
                        >
                          <Link2 className="h-4 w-4" />
                          Привязать
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        {!q.isLoading && !q.isError && items.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            Очередь пуста — все термины распознаны.
          </div>
        )}
      </Card>

      <p className="text-[11px] text-muted-foreground">
        Привязка создаёт alias → будущие импорты резолвятся автоматически.
        Непривязанные термины не участвуют в пользовательском поиске.
      </p>

      <ResolveDialog item={resolving} onClose={() => setResolving(null)} />
    </div>
  );
}
